import { buildAccountTypeMeta } from '../../../auth/partnerSignupMeta';
import { getCompanyTypeOption } from '../../../auth/partnerSignupBusiness';

export { buildAccountTypeMeta };

export const PARTNER_DOC_KEYS = [
  ['registrationCertificate', 'Registration certificate'],
  ['panCardDocument', 'PAN card'],
  ['bankPassbook', 'Bank passbook'],
  ['aadhaarFront', 'Aadhaar card'],
  ['shopPicture', 'Shop picture'],
  ['streetPicture', 'Street picture'],
  ['panUpload', 'PAN document'],
  ['gstCert', 'GST certificate'],
  ['aadhaarFront', 'Aadhaar front'],
  ['aadhaarBack', 'Aadhaar back'],
  ['shopLicense', 'Shop license'],
  ['addressProof', 'Address proof'],
  ['businessLogo', 'Business logo'],
];

export const KYC_FIELD_LABELS = {
  aadhaarNumber: 'Aadhaar Number',
  panNumber: 'PAN Number',
  panCardDocument: 'PAN Card Document',
  bankPassbook: 'Bank Passbook',
  aadhaarFront: 'Aadhaar Card',
  gstNumber: 'GST Number',
  registrationCertificate: 'Registration Certificate',
  shopPicture: 'Shop Picture',
  streetPicture: 'Street Picture',
  companyName: 'Company Name',
  ownerName: 'Owner Name',
  storeName: 'Store / Shop Name',
  shopName: 'Shop Name',
  shopNumber: 'Shop Phone',
  partnerId: 'Seller ID',
  stateCode: 'State code',
  businessSpecificationLabel: 'Business Type',
  businessSpecificationLabels: 'Business Categories',
  fullAddress: 'Full Address',
  houseNo: 'House / Shop No.',
  street: 'Street',
  landmark: 'Landmark',
  city: 'City',
  pinCode: 'PIN Code',
  locationPinCode: 'GPS PIN',
  contactEmail: 'Contact Email',
  sellerCategory: 'Seller Category (id)',
  sellerCategoryLabel: 'Account Type',
  accountTypeLabel: 'Account Type (full)',
  accountMainType: 'Main Type',
  partCondition: 'Parts Condition',
  bankAccountHolder: 'Account Holder',
  bankAccountNumber: 'Account Number',
  bankIfsc: 'IFSC',
  bankName: 'Bank Name',
  verificationStatus: 'Verification Status',
  latitude: 'GPS Latitude',
  longitude: 'GPS Longitude',
  area: 'Area',
  state: 'State',
  country: 'Country',
  postalCode: 'Postal Code',
};

const REGISTRATION_SKIP_KEYS = new Set([
  'onboardingPending',
  'gstVerification',
  'bankVerification',
  'partsOnlyDeclared',
  'payoutHeld',
  'fraudReviewRequired',
  'dashboardTools',
  'sellerMode',
  'sellerModeLabel',
  'sellerOnboarding',
  'listingMode',
  'modeRevalidatedAt',
  'approximateCarCount',
  'accountNumberLast4',
  'accountHolderName',
  'ifscCode',
  'phone',
  'businessName',
  'businessSpecifications',
  'businessSpecificationOther',
]);

const PARTNER_SIGNAL_KEYS = [
  'companyName', 'ownerName', 'shopName', 'storeName', 'panNumber', 'aadhaarNumber',
  'houseNo', 'street', 'city', 'pinCode', 'fullAddress', 'gstNumber', 'companyType',
  'registrationCertificate', 'panCardDocument', 'bankPassbook', 'aadhaarFront', 'shopPicture', 'streetPicture', 'bankAccountHolder',
  'bankAccountNumber', 'bankIfsc', 'bankName', 'contactEmail', 'sellerCategory',
  'sellerCategoryLabel', 'accountTypeLabel', 'businessSpecificationLabel',
  'businessSpecificationLabels', 'landmark', 'locationPinCode', 'latitude', 'longitude',
  'partnerId', 'state', 'stateCode',
];

const isDocPath = (v) => typeof v === 'string' && (v.startsWith('/uploads/') || v.startsWith('uploads/') || v.startsWith('http://') || v.startsWith('https://'));
const getDocValue = (v) => (v && typeof v === 'object' && v.path != null ? v.path : v);

export function formatPartnerFieldKey(k) {
  return KYC_FIELD_LABELS[k] || String(k).replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

function hasMeaningfulRegistrationData(blob) {
  if (!blob || typeof blob !== 'object') return false;
  return PARTNER_SIGNAL_KEYS.some((key) => {
    const v = blob[key];
    if (v == null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'boolean') return false;
    return true;
  });
}

export function pickRegistrationBlob(user) {
  const role = (user?.role || '').toLowerCase();
  if (role === 'garage' && hasMeaningfulRegistrationData(user?.garageDetails)) {
    return user.garageDetails;
  }
  if (hasMeaningfulRegistrationData(user?.vendorDetails)) {
    return user.vendorDetails;
  }
  if (hasMeaningfulRegistrationData(user?.usedSellerDetails)) {
    return user.usedSellerDetails;
  }
  if (user?.mechanicDetails && hasMeaningfulRegistrationData(user.mechanicDetails)) return user.mechanicDetails;
  if (user?.shippingDetails && hasMeaningfulRegistrationData(user.shippingDetails)) return user.shippingDetails;
  return null;
}

function maskSensitiveValue(key, value) {
  const k = String(key).toLowerCase();
  const str = String(value ?? '');
  if (!str) return '—';
  if (k.includes('aadhaar') && str.length >= 4) return `XXXX-XXXX-${str.slice(-4)}`;
  if ((k.includes('accountnumber') || k === 'bankaccountnumber') && str.length >= 4) return `••••${str.slice(-4)}`;
  return str;
}

export function buildRegistrationSections(user) {
  const blob = pickRegistrationBlob(user);
  if (!blob) return [];

  const account = {};
  const business = {};
  const address = {};
  const location = {};
  const bank = {};
  const other = {};

  Object.entries(blob).forEach(([key, raw]) => {
    if (REGISTRATION_SKIP_KEYS.has(key)) return;
    if (PARTNER_DOC_KEYS.some(([k]) => k === key)) return;
    if (raw == null || raw === '') return;
    if (typeof raw === 'object' && !Array.isArray(raw)) return;

    let value = raw;
    if (Array.isArray(raw)) value = raw.filter(Boolean).join(', ');
    if (key === 'companyType') {
      value = getCompanyTypeOption(raw)?.label || raw;
    }
    if (key === 'aadhaarNumber' || key === 'garageAadhaarNumber' || key === 'ownerAadhaarNumber' || key === 'bankAccountNumber') {
      value = maskSensitiveValue(key, value);
    }

    const lk = key.toLowerCase();
    if (['sellercategory', 'sellercategorylabel', 'accounttypelabel', 'accountmaintype', 'partcondition'].includes(lk)) {
      account[key] = value;
    } else if (lk === 'latitude' || lk === 'longitude' || lk === 'locationpincode') {
      location[key] = value;
    } else if (['houseno', 'street', 'landmark', 'city', 'pincode', 'fulladdress'].includes(lk) || lk.includes('address')) {
      address[key] = value;
    } else if (lk.startsWith('bank') || lk.includes('ifsc') || lk.includes('account')) {
      bank[key] = value;
    } else if (
      ['companyname', 'ownername', 'storename', 'shopname', 'shopnumber', 'garagename', 'garageaadhaarnumber',
        'owneraadhaarnumber', 'ownerpannumber', 'companytype', 'gstnumber', 'pannumber',
        'businessspecificationlabel', 'businessspecificationlabels', 'contactemail', 'verificationstatus'].includes(lk)
      || lk.includes('business')
    ) {
      business[key] = value;
    } else if (key === 'pinCodeMismatch') {
      other[key] = raw ? 'Yes' : 'No';
    } else {
      other[key] = value;
    }
  });

  const sections = [];
  if (Object.keys(account).length) sections.push({ title: 'Account type (Step 1)', data: account });
  if (Object.keys(business).length) sections.push({ title: 'Business details (Step 2)', data: business });
  if (Object.keys(address).length) sections.push({ title: 'Shop address', data: address });
  if (Object.keys(location).length) sections.push({ title: 'GPS / Location', data: location });
  if (Object.keys(bank).length) sections.push({ title: 'Bank details', data: bank });
  if (Object.keys(other).length) sections.push({ title: 'Other', data: other });

  if (user?.partnerId) {
    const businessSection = sections.find((s) => s.title === 'Business details (Step 2)');
    if (businessSection) {
      businessSection.data = { partnerId: user.partnerId, ...businessSection.data };
    } else {
      sections.unshift({ title: 'Seller ID', data: { partnerId: user.partnerId } });
    }
  }

  const addr = user?.address;
  if (addr && typeof addr === 'object') {
    const addrData = {};
    ['area', 'city', 'state', 'country', 'postalCode', 'fullAddress'].forEach((k) => {
      if (addr[k]) addrData[k] = addr[k];
    });
    if (Object.keys(addrData).length) {
      sections.push({ title: 'Profile address', data: addrData });
    }
  }

  return sections;
}

export function collectRegistrationDocuments(user) {
  const blob = pickRegistrationBlob(user);
  const found = [];
  const seen = new Set();

  const add = (path, label) => {
    const p = getDocValue(path);
    if (!p || !isDocPath(p) || seen.has(p)) return;
    seen.add(p);
    found.push({ path: p, label });
  };

  if (blob) {
    PARTNER_DOC_KEYS.forEach(([key, label]) => add(blob[key], label));
    Object.entries(blob).forEach(([key, val]) => {
      const p = getDocValue(val);
      if (isDocPath(p) || (typeof val === 'string' && isDocPath(val))) {
        add(val, formatPartnerFieldKey(key));
      }
    });
  }

  return found;
}

export function isPendingPartnerApplication(user) {
  const role = (user?.role || '').toLowerCase();
  const vd = user?.vendorDetails || {};
  const gd = user?.garageDetails || {};
  if (role === 'used_pending') return true;
  if (user?.onboardingComplete === false && (role === 'vendor' || role === 'garage')) return true;
  if (vd.verificationStatus === 'pending_review' || vd.onboardingPending === true) return true;
  if (gd.verificationStatus === 'pending_review' || gd.onboardingPending === true) return true;
  if ((user?.kycStatus || 'Pending') === 'Pending' && hasMeaningfulRegistrationData(vd)) return true;
  return false;
}

export function getPartnerDisplayName(user) {
  return (
    user?.vendorDetails?.storeName
    || user?.vendorDetails?.companyName
    || user?.vendorDetails?.ownerName
    || user?.garageDetails?.garageName
    || user?.usedSellerDetails?.yardShopName
    || user?.name
    || '—'
  );
}

export function getPartnerPublicId(user) {
  return user?.partnerId || user?.vendorDetails?.partnerId || user?.garageDetails?.partnerId || '';
}

export function getPartnerAccountTypeLabel(user) {
  const blob = pickRegistrationBlob(user);
  return blob?.accountTypeLabel || blob?.sellerCategoryLabel || blob?.sellerCategory || '—';
}
